<template>
  <v-container id="fileList" fluid class="text-center black--text subtitle-1">
    <v-row>
      <v-col>
        <v-progress-linear :active="innerLoading" color="loader" indeterminate />
      </v-col>
    </v-row>
    <ValidationObserver ref="filesList">
      <v-data-iterator
          :items="documentList"
          hide-default-footer
          disable-pagination
          disable-filtering
          item-key="documentType"
          :locale="systemLocale">
        <template v-slot="props">
          <v-row justify="center">
            <v-col
                v-for="item in props.items"
                :key="item.documentType"
                cols="12"
                sm="10"
                md="5"
                xl="4">
              <v-card>
                <v-card-title>
                  <v-row
                      class="v-label textcolor--text subtitle-2 font-weight-bold">
                    <v-col
                        v-if="item.required"
                        cols="1"
                        class="red--text"
                        align-self="center">
                      *
                    </v-col>
                    <v-col
                        :cols="item.required
                          ? (item.fileId ? 8: 9 )
                          : 10"
                        class="py-1 text-left"
                        align-self="center">
                      <link-with-tip
                          v-if="item.templatePath"
                          :href="item.templatePath"
                          :tip="$t('Скачать шаблон документа')"
                          :text="$t(item.documentDescription)" />
                      <span v-else class="wb">
                        {{ $t(item.documentDescription) }}
                      </span>
                    </v-col>
                    <v-col
                        cols="1"
                        class="text-right"
                        align-self="center">
                      <typed-file-upload
                          v-if="!readonly"
                          :document-type="item.documentType"
                          :document-group="item.documentGroup"
                          :disabled="innerLoading"
                          :upload-method="uploadIdentityFile" />
                    </v-col>
                    <v-col
                        cols="1"
                        class="text-right"
                        align-self="center">
                      <btn-icon-tip
                          v-if="item.fileId && !readonly"
                          :id="`deleteFile-${item.fileId}`"
                          icon="delete"
                          :disabled="innerLoading"
                          :tip="$t('Удалить файл')"
                          @click="removeFile(item.fileId)" />
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-divider />
                <v-row
                    class="v-label textcolor--text font-weight-bold py-3 pr-1">
                  <v-col
                      cols="12"
                      class="text-left px-6">
                    <ValidationObserver :ref="item.documentType">
                      <validatable-text-input
                          :key="item.documentType"
                          v-model="item.fileName"
                          :rules="{ required: !!item.required }"
                          :label="$t(item.documentDescription)"
                          prepend-icon="attach_file"
                          :vid="item.documentType"
                          readonly />
                    </ValidationObserver>
                  </v-col>
                </v-row>
                <v-divider v-if="item.fileId && item.metadata.length" />
                <v-card-text>
                  <v-row v-if="item.fileId">
                    <v-col cols="12">
                      <file-meta-data
                          :key="item.documentType"
                          ref="metadata"
                          :document="item"
                          :readonly="readonly"
                          :file-exist="!!item.fileId" />
                    </v-col>
                  </v-row>
                  <v-divider v-if="item.fileId" />
                  <v-row v-if="item.fileId">
                    <v-col cols="12" class="text-center">
                      <btn-icon-tip
                          :id="`getFile-${item.fileId}`"
                          icon="get_app"
                          :tip=" $t('Скачать документ')"
                          @click="getIdentityFile(item.fileId)" />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-data-iterator>
    </ValidationObserver>
  </v-container>
</template>

<style scoped>
.wb {
  word-break: break-word !important;
}
</style>

<script>
  import Vue from 'vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import TypedFileUpload from '@/components/reused/ui/TypedFileUpload.vue'
  import FileMetaData from './FileMetaData.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import LinkWithTip from '@/components/reused/ui/LinkWithTip.vue'
  import getFileMixin from '@/utils/mixins/scoped/getFileMixin.js'
  import { mapActions } from 'vuex'
  import { IDENTITY_FILES } from '@/constants/store.js'

  export default {
    components: {
      TypedFileUpload,
      BtnIconTip,
      LinkWithTip,
      ValidatableTextInput,
      FileMetaData
    },
    mixins: [
      getFileMixin('isLoading')
    ],
    props: {
      documentTypes: {
        type: Array,
        default: () => {
          return []
        }
      },
      requestFiles: {
        type: Array,
        default: () => {
          return []
        }
      },
      readonly: {
        type: Boolean,
        default: false
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isLoading: false
      }
    },
    computed: {
      innerLoading () {
        return this.isLoading || this.loading
      },
      documentList () {
        const vm = this
        const requiredDocumentTypes = vm.documentTypes || []
        const requestFiles = vm.requestFiles || []
        const list = requiredDocumentTypes.map(item => {
          const templateName = vm.getDocumentTemplateName(item.documentType, vm.systemLocale)
          const templatePath = templateName
            ? vm.getSystemFileUrl(templateName)
            : null
          const savedFile = requestFiles.find(f => f.documentType === item.documentType)

          const res = savedFile
            ? {
              ...item,
              ...savedFile,
              templatePath
            }
            : {
              ...item,
              templatePath,
              data: {}
            }
          return res
        })
        return list
      }
    },
    methods: {
      ...mapActions(IDENTITY_FILES.NAMESPACE, {
        uploadIdentityFileAction: IDENTITY_FILES.ACTIONS.UPLOAD_IDENTITY_FILE,
        deleteIdentityFileAction: IDENTITY_FILES.ACTIONS.DELETE_IDENTITY_FILE
      }),
      uploadIdentityFile: async function (form, documentType, documentGroup) {
        // try catch не нужен (если ошибка, обработаем в месте вызова метода)
        const uploadFileResult = await this.uploadIdentityFileAction({
          form,
          documentType
        })
        await this.$refs[documentType][0].validate()

        this.$emit('uploadFile', uploadFileResult.fileId, documentType, documentGroup)
      },
      validate: async function () {
        const result = await this.$refs.filesList.validate()
        if (!result) {
          throw new Vue.errorsTypes.ValidationError()
        }
      },
      removeFile: async function (fileId) {
        try {
          this.isLoading = true
          await this.deleteIdentityFileAction({
            fileId
          })
          this.$emit('deleted')
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      },
      syncronizeWithStoreData: async function () {
        const vm = this
        vm.isLoading = true
        if (vm.$refs.metadata) {
          for (const ref of vm.$refs.metadata) {
            if (ref.syncronizeFieldsData) {
              await ref.syncronizeFieldsData()
            }
          }
        }
        vm.isLoading = false
      }
    }
  }
</script>
